import React from 'react'
import { Link } from 'react-router-dom'
import Badge from '../../../../components/Badge'
import QueryError from '../../../conductor/QueryError'
import { VerdictLine } from '../../../conductor/conductorVerdicts'
import { TD, TH } from '../../../conductor/conductorTableCells'
import {
  CONDUCTOR_ACCOUNT_PAGE_SIZE,
  hasCadenceInstance,
  sortVerdictsFailFirst,
} from '../../../conductor/conductorAccountSignal'
import { formatConductorStage, formatConductorTimestamp } from '../../../conductor/conductorUtils'
import {
  useGetConductorInstancesQuery,
  useGetConductorSubjectVerdictsQuery,
} from '../../../conductor/conductorApi'

import type { ConductorInstance } from '../../../conductor/conductor.types'

export interface TabConductorProps {
  title: string
  badge?: number | string
  description?: string
  tenantAccountCuid: string
}

const InstanceRow: React.FC<{ instance: ConductorInstance }> = ({ instance }) => (
  <tr className="border-t border-slate-100 dark:border-slate-700">
    <TD className="font-medium">
      <Link
        to={`/conductor/instances/${instance.cuid}`}
        className="text-indigo-600 hover:underline dark:text-indigo-400"
      >
        {instance.playbookKey}
      </Link>
    </TD>
    <TD>
      <Badge status={instance.status} />
    </TD>
    <TD>{formatConductorStage(instance.currentStage)}</TD>
    <TD>{instance.blockedReason ?? '—'}</TD>
    <TD align="right">{instance.openWorkItems}</TD>
    <TD align="right">{formatConductorTimestamp(instance.modifiedAt)}</TD>
  </tr>
)

export const TabConductor: React.FC<TabConductorProps> = ({
  title: _title,
  badge: _badge,
  description: _description,
  tenantAccountCuid,
}) => {
  const instancesQuery = useGetConductorInstancesQuery({
    subjectCuid: tenantAccountCuid,
    page: 1,
    resultsPerPage: CONDUCTOR_ACCOUNT_PAGE_SIZE,
  })
  const verdictsQuery = useGetConductorSubjectVerdictsQuery(tenantAccountCuid)
  const instances = instancesQuery.data?.results ?? []
  const instanceTotal = instancesQuery.data?.total ?? 0
  const showCadence = hasCadenceInstance(instances)
  const verdicts = sortVerdictsFailFirst(verdictsQuery.data ?? [])

  if (instancesQuery.isError) {
    return <QueryError message="Failed to load Conductor workflows." onRetry={() => void instancesQuery.refetch()} />
  }

  if (instancesQuery.data == null) {
    return <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4">
        {showCadence ? (
          <Link to="/conductor/cadence" className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
            Open Cadence queue
          </Link>
        ) : null}
        <Link
          to={`/conductor/outbox?subjectCuid=${encodeURIComponent(tenantAccountCuid)}`}
          className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Open Outbox
        </Link>
        <Link
          to={`/conductor/inbox?subjectCuid=${encodeURIComponent(tenantAccountCuid)}`}
          className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
        >
          Open Inbox
        </Link>
      </div>

      {instanceTotal > instances.length ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Showing {instances.length} of {instanceTotal} workflows.
        </p>
      ) : null}

      {instances.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
          No Conductor workflows on this account.
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              <TH>Playbook</TH>
              <TH>Status</TH>
              <TH>Stage</TH>
              <TH>Blocked reason</TH>
              <TH align="right">Open items</TH>
              <TH align="right">Updated</TH>
            </tr>
          </thead>
          <tbody>
            {instances.map((instance) => (
              <InstanceRow key={instance.cuid} instance={instance} />
            ))}
          </tbody>
        </table>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">Gate verdicts</h3>
        {verdictsQuery.isError ? (
          <QueryError message="Failed to load gate verdicts." onRetry={() => void verdictsQuery.refetch()} />
        ) : verdictsQuery.data == null ? (
          <div className="text-xs text-slate-400 dark:text-slate-500">Loading…</div>
        ) : verdicts.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500">No verdicts recorded.</div>
        ) : (
          <ul className="space-y-1">
            {verdicts.map((verdict) => (
              <VerdictLine key={`${verdict.gateId}-${verdict.sweepCuid}`} verdict={verdict} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default TabConductor
